import { Injectable } from '@angular/core';
import { AzureKeyCredential, SearchClient } from '@azure/search-documents';
import { from, Observable } from 'rxjs';
import { AzureSearchConfig } from './azure-search.config.js';
import {
	IAzureFoodSearchItemRaw,
	IAzureSearchInput,
	IAzureSearchResponse,
	IAzureFoodSearchItem
} from '../search.models.js';
import { environment } from 'src/app/environments/environment.js';

@Injectable({
	providedIn: 'root'
})
export class AzureSearchService {
	private readonly foodsIndexName: string = environment.azureSearch.indexes.foods;
	private readonly foodsClient: SearchClient<IAzureFoodSearchItemRaw>;

	constructor(private config: AzureSearchConfig) {
		this.foodsClient = new SearchClient(
			this.config.endpoint,
			this.foodsIndexName,
			new AzureKeyCredential(this.config.queryKey)
		);
	}

	searchFoodItems(data: IAzureSearchInput): Observable<IAzureSearchResponse<IAzureFoodSearchItem>> {
		return from(this.searchFoodItemsInternal(data));
	}

	private getSearchQuery(search: string | undefined): string | undefined {
		if (!search) {
			return undefined;
		}

		const terms: string[] = search
			.trim()
			.split(' ')
			.filter((m) => m?.length)
			.map((m) => `${m}*`); // https://learn.microsoft.com/en-us/azure/search/search-query-fuzzy

		return terms.join(' ');
	}

	private async searchFoodItemsInternal(
		data: IAzureSearchInput
	): Promise<IAzureSearchResponse<IAzureFoodSearchItem>> {
		let skip: number | undefined = undefined;

		if (data.page === 1) {
			skip = 0;
		} else if (data.page > 1) {
			skip = (data.page - 1) * data.pageSize;
		}

		const items: IAzureFoodSearchItem[] = [];
		const searchResults = await this.foodsClient.search(this.getSearchQuery(data.search), {
			top: data.pageSize,
			skip: skip,
			includeTotalCount: true,
			highlightFields: 'name',
			queryType: 'full',
			searchMode: 'all',
			searchFields: ['name', 'category'], // searching trough category increases search relevance
			select: ['carbs', 'id', 'name', 'category', 'kcal', 'proteins', 'sugars', 'fats', 'fiber']
		});

		for await (const result of searchResults.results) {
			const document = result.document;
			let highlight: string = document.name;

			if (result.highlights?.name?.length) {
				highlight = result.highlights.name[0];
			}

			items.push({
				score: result.score,
				id: document.id,
				fiber: document.fiber,
				carbs: document.carbs,
				fats: document.fats,
				kcal: document.kcal,
				name: document.name,
				titleHighlighted: highlight,
				proteins: document.proteins,
				sugars: document.sugars,
				category: document.category
			});
		}

		return {
			items: items,
			totalCount: searchResults.count ?? 0
		};
	}
}
